import { ACCOUNT_MAX_CHAR } from "../AccountingAccount/accountingAccountOptions";
import { accountTypeOptions, UNIQUE_RELATION_TYPES } from "./accountingAccountRelationOptions";

/**
 * Filtra las cuentas contables que son válidas para asignar en una relación.
 *
 * Regla de negocio: sólo puede asignarse una cuenta cuyo "número" tenga la
 * longitud exacta de una cuenta de último nivel (`ACCOUNT_MAX_CHAR`). Las
 * cuentas con número más corto son agrupadoras/intermedias y no deben poder
 * seleccionarse en el formulario de relación.
 *
 * Alimenta tanto las opciones del selector "Cuenta Contable" del formulario
 * como la validación previa de la vista (avisar cuando la planta no tiene
 * ninguna cuenta asignable).
 *
 * @param {Array<{ number?: String }>} accountList - Cuentas contables de la planta.
 * @returns {Array} Subconjunto de `accountList` con número de longitud exacta;
 *                   el mismo arreglo recibido si viene vacío.
 */
export const getValidAccounts = (accountList) => {
  if (!accountList.length) return accountList;
  // Solo se puede asignar cuentas cuyo "número" sea igual a 46 caracteres.
  return accountList.filter((acc) => acc.number?.length === ACCOUNT_MAX_CHAR);
};

/**
 * Opciones del selector "Cuenta Contable".
 *
 * En el alta son sólo las cuentas válidas (`getValidAccounts`). Al editar una
 * relación heredada cuya cuenta ya no es asignable -su número no mide
 * `ACCOUNT_MAX_CHAR` o la cuenta fue eliminada y el catálogo, que sólo trae
 * `status = 1`, ya no la incluye- se agrega esa cuenta marcada como
 * "(no asignable)" para que el campo no quede vacío. Queda seleccionable: la
 * única no asignable es la original, y elegirla equivale a conservarla.
 *
 * @param {Array} accountList - Cuentas contables de la planta.
 * @param {{ idAccountingAccount?: String, accountingAccountName?: String,
 *           accountingAccountNumber?: String }} relation - Relación en edición;
 *        vacío en el alta.
 * @returns {Array} Cuentas válidas y, si aplica, la cuenta actual de la relación.
 */
export const buildAccountOptions = (accountList = [], relation = {}) => {
  const validAccounts = getValidAccounts(accountList);
  const { idAccountingAccount } = relation;
  if (!idAccountingAccount || validAccounts.some((acc) => acc.id === idAccountingAccount)) {
    return validAccounts;
  }

  const current = accountList.find((acc) => acc.id === idAccountingAccount) || {
    id: idAccountingAccount,
    name: relation.accountingAccountName,
    number: relation.accountingAccountNumber,
  };

  return [
    ...validAccounts,
    { ...current, label: `${current.name} | ${current.number} (no asignable)` },
  ];
};

/**
 * Opciones del selector "Tipo" con los tipos únicos ya ocupados deshabilitados.
 *
 * Regla de negocio: los tipos de `UNIQUE_RELATION_TYPES` sólo pueden asignarse
 * a una cuenta por compañía y planta, porque la Póliza toma el primer registro
 * que encuentra. La opción ocupada muestra a qué cuenta está asignada para que
 * el usuario sepa qué relación editar.
 *
 * @param {Array} relations - Relaciones de la planta cargada.
 * @param {{ idCompany?: String, excludeId?: String }} params - Compañía de la
 *        planta y, al editar, id de la propia relación (su tipo queda disponible).
 * @returns {Array} Copia de `accountTypeOptions`; las opciones ocupadas traen
 *                   `disabled: true` y la cuenta asignada en la etiqueta.
 */
export const buildAccountTypeOptions = (relations = [], { idCompany, excludeId } = {}) =>
  accountTypeOptions.map((option) => {
    if (!UNIQUE_RELATION_TYPES.includes(option.value)) return option;

    const assigned = relations.find((relation) =>
      relation.type === option.value &&
      relation.idCompany === idCompany &&
      relation.id !== excludeId,
    );
    if (!assigned) return option;

    return {
      ...option,
      label: `${option.label} — asignado a ${assigned.accountingAccountName}`,
      disabled: true,
    };
  });
